import {
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationAdminDetails } from 'types/admin';
interface OrganizationBannerProps {
  organization: OrganizationAdminDetails;
  onPeriodChange?: (period: 'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks') => void;
}

const OrganizationBanner: React.FC<OrganizationBannerProps> = ({ organization, onPeriodChange }) => {
  const [period, setPeriod] = useState<'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks'>(organization.periodInfo.period as 'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks');
  const { t } = useTranslation();

  const handlePeriodChange = (newPeriod: 'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks') => {
    setPeriod(newPeriod);
    onPeriodChange?.(newPeriod);
  };

  const formatProgression = (percentage: number) => {
    if (percentage === 0) return '0%';
    if (percentage > 0) return `+${percentage.toFixed(1)}%`;
    return `${percentage.toFixed(1)}%`;
  };

  const getProgressionColor = (percentage: number) => {
    if (percentage > 0) return '#4caf50';
    if (percentage < 0) return '#f44336';
    return '#757575';
  };

  return (
    <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
      <CardContent sx={{ py: 1, px: 2 }}>
        {/* Header with Organization Name and Period Filter */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.25 }}>
              {organization.company_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {organization.id} • {organization.hasRecentActivity ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
            <Select
              value={period}
              label="Period"
              onChange={(e) => handlePeriodChange(e.target.value as 'month' | 'semester' | 'year')}
            >
              <MenuItem value="week">{t('PERIOD_WEEK')}</MenuItem>
              <MenuItem value="two_weeks">{t('PERIOD_TWO_WEEKS')}</MenuItem>
              <MenuItem value="month">{t('PERIOD_MONTH')}</MenuItem>
              <MenuItem value="quarter">{t('PERIOD_QUARTER')}</MenuItem>
              <MenuItem value="semester">{t('PERIOD_SEMESTER')}</MenuItem>
              <MenuItem value="year">{t('PERIOD_YEAR')}</MenuItem>
            </Select>
        </Box>

        {/* Metrics Grid */}
        <Grid container spacing={1}>
          {/* Members */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 0.25 }}>
                {organization.memberCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {t('ADMIN_STATS_TOTAL_MEMBERS')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {organization.activeMemberCount} {t('ADMIN_STATS_ACTIVE')}
              </Typography>
            </Box>
          </Grid>

          {/* Projects */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ed6c02', mb: 0.25 }}>
                {organization.projectCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {t('ADMIN_STATS_TOTAL_PROJECTS')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: getProgressionColor(organization.projectProgression.progressionPercentage), fontWeight: 'bold' }} dir="ltr">
                  {formatProgression(organization.projectProgression.progressionPercentage)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {organization.projectProgression.currentPeriodCount} / {organization.projectProgression.previousPeriodCount}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Revisions */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 0.25 }}>
                {organization.revisionCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {t('ADMIN_STATS_TOTAL_REVISIONS')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: getProgressionColor(organization.revisionProgression.progressionPercentage), fontWeight: 'bold' }} dir="ltr">
                  {formatProgression(organization.revisionProgression.progressionPercentage)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {organization.revisionProgression.currentPeriodCount} / {organization.revisionProgression.previousPeriodCount}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Credits */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 0.25 }}>
                {organization.totalDebits}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {t('ADMIN_STATS_TOTAL_CREDITS')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: getProgressionColor(organization.transactionProgression.progressionPercentage), fontWeight: 'bold' }} dir="ltr">
                  {formatProgression(organization.transactionProgression.progressionPercentage)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {organization.transactionProgression.currentPeriodCount} / {organization.transactionProgression.previousPeriodCount}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OrganizationBanner; 